import { Link } from "@inertiajs/react";
import {
  Brain,
  ChevronRight,
  Crown,
  Monitor,
  UserCheck,
  Users,
  Zap,
} from "lucide-react";
import BizminerLayout from "@/bizminer/layouts/app/BizminerLayout.tsx";
import { Card, CardContent } from "@/components/ui/card";
import { bizminer_app_pillar_insight_functional_insight_path } from "@/routes";
import type { BizminerPillarInsight } from "@/types/serializers";

interface Props {
  pillar_insight: BizminerPillarInsight;
}

const iconMap = {
  users: Users,
  "user-check": UserCheck,
  monitor: Monitor,
  crown: Crown,
  brain: Brain,
  zap: Zap,
};

interface CategoryCardProps {
  category: BizminerPillarInsight["analysis"]["categories"][number];
  pillarInsightId: number;
}

function CategoryCard({ category, pillarInsightId }: CategoryCardProps) {
  const score = category.account_score;
  const kpiCount = category.kpi_drivers?.length ?? 0;

  return (
    <Link
      href={bizminer_app_pillar_insight_functional_insight_path(
        pillarInsightId,
        category.id,
      )}
    >
      <Card className="transition-[box-shadow,background-color,border-color] hover:shadow-md hover:bg-muted/20 hover:border-foreground/20 cursor-pointer h-full">
        <CardContent className="px-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="font-semibold text-base text-foreground">
              {category.name}
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {kpiCount} KPI driver{kpiCount !== 1 ? "s" : ""}
            </div>
            <div className="text-sm">
              <span className="text-muted-foreground">Score </span>
              <span className="font-semibold text-foreground">
                {score} / 5.0
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function FunctionalInsights({ pillar_insight }: Props) {
  const IconComponent =
    iconMap[pillar_insight.icon as keyof typeof iconMap] || Users;
  const categories = pillar_insight.analysis.categories;

  return (
    <BizminerLayout>
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <div className="flex size-12 items-center justify-center rounded-lg border border-border/50 bg-muted/30">
            <IconComponent className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Functional Insights
            </h1>
            <p className="text-muted-foreground">{pillar_insight.title}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category: any) => (
            <CategoryCard
              key={category.id}
              category={category}
              pillarInsightId={pillar_insight.id}
            />
          ))}
        </div>
      </div>
    </BizminerLayout>
  );
}
